import { seasonPagePath } from 'constants/seasonRollover.mjs';
import { resolveEventPageTicker } from 'constants/syntheticMarginEvents';

import { upperFirstLetter } from './caseing';
import { getSeriesPathDescription, getSeriesRelativeUrl, sportSeriesRedirects } from './series';

export enum MarketForm {
    BINARY = 'binary',
    CATEGORICAL = 'categorical',
    NUMERICAL = 'numerical',
    FINANCIAL_UNDERLYING = 'financial-underlying',
}

export const getEventRelativeUrl = (
    seriesTicker: string,
    seriesTitle?: string,
    eventTicker?: string,
    marketTicker?: string,
    /** Preselects the order panel's side alongside the `marketTicker` deep link. */
    orderSide?: 'yes' | 'no',
    /**
     * `exactSeason` keeps a rollover book's season on its own URL — only for a client switch that
     * must land on that exact season (`useSwitchEvent`). `pinSeason` keeps the series page but names
     * the season (`?season=kxsb-26`) when no `marketTicker` does, for a link that stands for one
     * season, such as a position's (`getPinnedEventRelativeUrl`).
     */
    { exactSeason = false, pinSeason = false }: { exactSeason?: boolean; pinSeason?: boolean } = {},
): string => {
    const baseUrl = getSeriesRelativeUrl(seriesTicker, seriesTitle);
    const query = [marketTicker && `marketTicker=${marketTicker}`, orderSide && `orderSide=${orderSide}`]
        .filter(Boolean)
        .join('&');
    const queryParams = query ? `?${query}` : '';

    // Any season of a rollover book links to the book's series page, which renders the current
    // season, or the `marketTicker`'s (`constants/seasonRollover.mjs`).
    const seasonPage = exactSeason
        ? undefined
        : seasonPagePath(seriesTicker, eventTicker, seriesTitle ? getSeriesPathDescription(seriesTitle) : undefined);
    if (seasonPage) {
        if (!pinSeason || marketTicker || !eventTicker) return `${seasonPage}${queryParams}`;
        return `${seasonPage}?${[`season=${eventTicker.toLowerCase()}`, query].filter(Boolean).join('&')}`;
    }

    // Use slash-based URL structure instead of hash-based
    if (eventTicker) {
        const mappedSeriesTicker = sportSeriesRedirects[seriesTicker] ?? seriesTicker;
        const mappedEventTicker = resolveEventPageTicker(eventTicker).replace(seriesTicker, mappedSeriesTicker);

        return `${baseUrl}/${mappedEventTicker.toLowerCase()}${queryParams}`;
    }

    return `${baseUrl}${queryParams}`;
};

/**
 * A link that stands for one particular event — a position, an order, a post, a watchlist entry —
 * rather than for the market in general. For a season of a rollover book that is the book's series
 * page naming the season (`?season=kxsb-26`), so a past season's holding still opens that season;
 * anything else gets the event's own URL, as `getEventRelativeUrl` builds it.
 */
export const getPinnedEventRelativeUrl = (seriesTicker: string, seriesTitle?: string, eventTicker?: string): string =>
    getEventRelativeUrl(seriesTicker, seriesTitle, eventTicker, undefined, undefined, { pinSeason: true });

/** `url` with `params` appended, whether or not it already carries a query. */
export const withQuery = (url: string, params: URLSearchParams): string => {
    const query = params.toString();
    return query ? `${url}${url.includes('?') ? '&' : '?'}${query}` : url;
};

/**
 * Carries a redirect's incoming search params onto `path`. Values already on `path`
 * (e.g. `marketTicker`) are overridden by a forwarded param of the same name.
 */
export const withForwardedSearchParams = (
    path: string,
    params: Record<string, string | string[] | undefined>,
): string => {
    const [pathname, existing] = path.split('?');
    const query = new URLSearchParams(existing);
    for (const [key, value] of Object.entries(params)) {
        if (Array.isArray(value)) value.forEach((item) => query.append(key, item));
        else if (value !== undefined) query.set(key, value);
    }
    const queryString = query.toString();
    return queryString ? `${pathname}?${queryString}` : pathname;
};

/**
 * Derive the event ticker from a Kalshi market ticker by dropping the
 * trailing `-MARKET[-…]` segment. Returns `undefined` for tickers without a
 * hyphen (rare single-market events whose market ticker is the event ticker).
 *
 * Example: `KXPGATOUR-CAC26-CAME` → `KXPGATOUR-CAC26`.
 */
export const deriveEventTickerFromMarketTicker = (marketTicker: string): string | undefined => {
    const i = marketTicker.lastIndexOf('-');
    return i > 0 ? marketTicker.slice(0, i) : undefined;
};

export const getMinimalSubtitle = (subtitle: string) => {
    let minimalSubtitle = subtitle;

    // Some prepositions can be inferred
    if (minimalSubtitle.slice(0, 3) === 'In ' || minimalSubtitle.slice(0, 3) === 'On ') {
        minimalSubtitle = upperFirstLetter(subtitle.slice(3));
    }

    return minimalSubtitle;
};
